// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Paper } from '@mantine/core';
import type { SearchRequest } from '@medplum/core';
import { formatSearchQuery } from '@medplum/core';
import type { Communication } from '@medplum/fhirtypes';
import type { ThreadBucket } from '@polarissleep/messaging';
import { normalizeCommunicationSearch } from '@polarissleep/messaging';
import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ThreadInbox } from '../../components/messages/ThreadInbox';

export function CommunicationTab(): JSX.Element {
  const { patientId, messageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const patientRef = `Patient/${patientId}`;

  const currentSearch = useMemo(() => (location.search ? location.search.substring(1) : ''), [location.search]);

  const params = useMemo(() => new URLSearchParams(currentSearch), [currentSearch]);

  const hasPatient = params.has('patient');

  const { normalizedSearch, parsedSearch, bucket } = useMemo(() => {
    const entries = Array.from(params.entries());
    if (!hasPatient) {
      entries.push(['patient', patientRef]);
    }
    const searchWithPatient = new URLSearchParams(entries).toString();
    return normalizeCommunicationSearch({
      search: searchWithPatient,
    });
  }, [hasPatient, params, patientRef]);

  useEffect(() => {
    if (normalizedSearch !== currentSearch) {
      const prefix = normalizedSearch ? `?${normalizedSearch}` : '';
      const threadPath = messageId ? `/${messageId}` : '';
      navigate(`/Patient/${patientId}/Communication${threadPath}${prefix}`, { replace: true })?.catch(console.error);
    }
  }, [currentSearch, messageId, navigate, normalizedSearch, patientId]);

  const onChange = (search: SearchRequest): void => {
    const fhirQuery = formatSearchQuery(search);
    const sep = fhirQuery.startsWith('?') ? '&' : '?';
    navigate(`/Patient/${patientId}/Communication${fhirQuery}${sep}bucket=${bucket}`)?.catch(console.error);
  };

  const getThreadUri = (topic: Communication): string => {
    const fhirQuery = formatSearchQuery(parsedSearch);
    const sep = fhirQuery.startsWith('?') ? '&' : '?';
    return `/Patient/${patientId}/Communication/${topic.id}${fhirQuery}${sep}bucket=${bucket}`;
  };

  const buildBucketUri = (value: ThreadBucket): string => {
    const fhirQuery = formatSearchQuery({ ...parsedSearch, offset: 0 });
    const sep = fhirQuery.startsWith('?') ? '&' : '?';
    return `/Patient/${patientId}/Communication${fhirQuery}${sep}bucket=${value}`;
  };

  const unreadUri = buildBucketUri('unread');
  const sentUri = buildBucketUri('sent');
  const allUri = buildBucketUri('all');

  const onNew = (message: Communication): void => {
    navigate(getThreadUri(message))?.catch(console.error);
  };

  return (
    <Paper
      withBorder
      radius="md"
      shadow="sm"
      p={0}
      mt="md"
      mx="md"
      mb="md"
      style={{ height: `calc(100vh - 98px - 2 * var(--mantine-spacing-md))`, overflow: 'hidden' }}
    >
      <ThreadInbox
        threadId={messageId}
        query={formatSearchQuery(parsedSearch).substring(1)}
        bucket={bucket}
        subject={{ reference: patientRef }}
        onNew={onNew}
        getThreadUri={getThreadUri}
        onChange={onChange}
        unreadUri={unreadUri}
        sentUri={sentUri}
        allUri={allUri}
      />
    </Paper>
  );
}
